import { Box, InputAdornment, makeStyles, TextField, Typography } from "@material-ui/core";
import * as React from "react";

import { UserUpdate } from "@/api/generated";
import { AuthErrorSummary } from "@/components/AuthFeedback";
import { BottomSheet } from "@/components/BottomSheet";
import { CustomButton } from "@/components/Button";
import { CustomChip } from "@/components/Chip";
import { CodeInput, CodeInputCell } from "@/components/CodeInput";
import { FormErrorText } from "@/components/FormErrorText";
import { layoutTokens, textStyles } from "@/designTokens";
import { useGetMasterInterestedPropertyTypes } from "@/hooks/useGetMasterData";
import { useSmsCodeAutofill } from "@/hooks/useSmsCodeAutofill";
import theme from "@/theme";
import { MONTH_OPTIONS, YEAR_OPTIONS } from "@/utilities/options";
import { smsPhoneNumberSchema } from "@/validations/phoneNumber";

import { useSendPhoneNumberAuthCode } from "../hooks/useSendPhoneNumberAuthCode";
import { useSettingsUserUpdate } from "../hooks/useSettingsUserUpdate";
import { SettingItemsProps } from "../SettingsPage";
import { ProfileSection } from "../utils/profileCompletion";
import {
  buildProfileUpdate,
  getProfileFields,
  getProfileValues,
  ProfileField,
  profileSectionTitles,
} from "../utils/profileFields";
import { profileSectionSchemas } from "../utils/profileSectionSchema";

import { useSettingsContentStyles } from "./SettingsContent";
import { SettingsFormRow } from "./SettingsFormRow";

const useStyles = makeStyles({
  chips: { display: "flex", flexWrap: "wrap", gap: layoutTokens.space[8], padding: "12px 0 16px" },
  label: { ...textStyles["SP/Body/Large"], paddingTop: layoutTokens.space[16] },
  options: { display: "flex", flexDirection: "column", gap: layoutTokens.space[8] },
  option: {
    minHeight: 48,
    border: 0,
    borderBottom: `1px solid ${theme.palette.semantic["border/default"]}`,
    textAlign: "left",
    padding: "12px 8px",
    background: "none",
    color: theme.palette.text.primary,
    font: "inherit",
    cursor: "pointer",
  },
  selected: { backgroundColor: theme.palette.semantic["state/selected-bg"], color: theme.palette.primary.main },
  input: { width: "100%", marginBottom: layoutTokens.space[16] },
  birthday: { display: "flex", gap: layoutTokens.space[16], "& > *": { flex: 1, minWidth: 0 } },
  sms: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    gap: layoutTokens.space[24],
    textAlign: "center",
  },
  smsTitle: {
    ...textStyles["SP/Heading/H3"],
    whiteSpace: "pre-line",
    [theme.breakpoints.up("md")]: textStyles["PC/Heading/H3"],
  },
  resend: { display: "flex", alignItems: "center", flexDirection: "column", gap: layoutTokens.space[8] },
});

export const ProfileEditor: React.FC<SettingItemsProps & { section: ProfileSection }> = ({
  user,
  setUser,
  masterResidenceTypes = [],
  section,
  onBack,
  onHeaderChange,
  onDraftChange,
}) => {
  const classes = useStyles();
  const contentClasses = useSettingsContentStyles();
  const [values, setValues] = React.useState(() => getProfileValues(user));
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [editing, setEditing] = React.useState<ProfileField | null>(null);
  const [pending, setPending] = React.useState<UserUpdate | null>(null);
  const [showSms, setShowSms] = React.useState(false);
  const [digits, setDigits] = React.useState(Array<string>(6).fill(""));
  const [smsError, setSmsError] = React.useState("");
  // SMS からの自動入力の待受をやり直した回数。新しいコードを送るたびに進める
  const [autofillRestartKey, setAutofillRestartKey] = React.useState(0);
  const inputs = React.useRef<(HTMLInputElement | null)[]>([]);
  const currentValues = React.useRef(values);
  currentValues.current = values;
  const availableCodePhone = React.useRef<string | null>(null);
  const attemptedCode = React.useRef("");
  const { update, error, loading } = useSettingsUserUpdate(setUser);
  const { sendPhoneNumberAuthCode, isSending, cooldownPhoneNumber, remaining } = useSendPhoneNumberAuthCode();
  const { masterInterestedPropertyTypes, loading: propertyTypesLoading } = useGetMasterInterestedPropertyTypes();
  const fields = getProfileFields(section, values, masterResidenceTypes);
  const cooldown = cooldownPhoneNumber === values.phone_number ? remaining : 0;
  const back = React.useCallback(() => {
    if (showSms) setShowSms(false);
    else onBack?.();
  }, [showSms, onBack]);
  React.useEffect(() => {
    onHeaderChange?.(showSms ? "電話番号の確認" : profileSectionTitles[section], back);
  }, [showSms, section, back, onHeaderChange]);

  React.useEffect(() => {
    onDraftChange?.(values);
  }, [values, onDraftChange]);
  React.useEffect(
    () => () => {
      onDraftChange?.(null);
    },
    [onDraftChange]
  );

  const setValue = (key: string, value: string | number | number[]) => {
    setValues((previous) => ({ ...previous, [key]: value }));
    setErrors((previous) => ({ ...previous, [key]: "" }));
  };
  const save = async () => {
    if (loading || isSending) return;
    const result = profileSectionSchemas[section].safeParse(values);
    const next: Record<string, string> = {};
    if (!result.success) {
      for (const issue of result.error.issues) next[String(issue.path[0])] = issue.message;
    }
    // 番号変更時は、桁数にかかわらずSMS用の案内を優先する。未変更の連絡先は従来どおり検証する。
    if (section === "personal" && user.phone_number !== values.phone_number) {
      const smsValidation = smsPhoneNumberSchema.safeParse(values.phone_number);
      if (!smsValidation.success) next.phone_number = smsValidation.error.issues[0].message;
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    const request = buildProfileUpdate(section, values);
    if (section === "personal" && user.phone_number !== values.phone_number) {
      const phone = String(values.phone_number);
      if (cooldown > 0 && availableCodePhone.current !== phone) {
        setSmsError(`認証コードはあと${cooldown}秒で再送できます。`);
        return;
      }
      if (availableCodePhone.current !== phone || cooldown === 0) {
        const sent = await sendPhoneNumberAuthCode(phone);
        if (!sent || currentValues.current.phone_number !== phone) return;
        if (!sent.success && !sent.codeAvailable) {
          setSmsError(sent.error);
          return;
        }
        availableCodePhone.current = phone;
        if (sent.success) {
          setDigits(Array(6).fill(""));
          attemptedCode.current = "";
          setAutofillRestartKey((key) => key + 1);
        }
      }
      setSmsError("");
      setPending(request);
      setShowSms(true);
      return;
    }
    const response = await update(request);
    if (response) {
      onBack?.();
    }
  };
  const confirmCode = async (code: string) => {
    if (!pending || code.length !== 6 || loading || attemptedCode.current === code) return;
    attemptedCode.current = code;
    const response = await update({ ...pending, phone_number_auth_code: Number(code) });
    if (response) {
      onBack?.();
    }
  };
  const changeCode = (index: number, input: string) => {
    const clean = input.replace(/\D/g, "");
    const next = [...digits];
    if (clean.length > 1) {
      clean
        .slice(0, 6 - index)
        .split("")
        .forEach((digit, offset) => {
          next[index + offset] = digit;
        });
    } else next[index] = clean;
    const code = next.join("");
    if (code !== digits.join("")) attemptedCode.current = "";
    setDigits(next);
    if (clean) inputs.current[Math.min(index + clean.length, 5)]?.focus();
    if (next.every(Boolean)) void confirmCode(code);
  };
  const resend = async () => {
    if (cooldown > 0 || isSending || loading) return;
    const result = await sendPhoneNumberAuthCode(String(values.phone_number));
    if (result?.success) {
      setDigits(Array(6).fill(""));
      attemptedCode.current = "";
      setSmsError("");
      inputs.current[0]?.focus();
      setAutofillRestartKey((key) => key + 1);
    } else if (result) setSmsError(result.error);
  };
  // 端末に届いた SMS からの自動入力（Trello #157）。確認中と再送の応答待ち（古い SMS を拾わないため）は待受を止め、
  // 再送で新しいコードを待ち直す
  useSmsCodeAutofill({
    enabled: showSms && !loading && !isSending,
    onCode: (code) => changeCode(0, code),
    restartKey: autofillRestartKey,
  });

  if (showSms)
    return (
      <Box className={classes.sms}>
        <Typography component="h2" className={classes.smsTitle}>
          {"認証コードを\n入力してください"}
        </Typography>
        <Typography className={contentClasses.caption}>
          {String(values.phone_number).slice(0, 3)}****{String(values.phone_number).slice(-4)} 宛に送信しました
        </Typography>
        <AuthErrorSummary message={smsError || error} />
        <CodeInput>
          {digits.map((digit, index) => (
            <CodeInputCell
              key={index}
              index={index}
              value={digit}
              disabled={loading}
              inputRef={(element) => {
                inputs.current[index] = element;
              }}
              onChange={(event) => changeCode(index, event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Backspace" && !digits[index]) inputs.current[Math.max(0, index - 1)]?.focus();
              }}
            />
          ))}
        </CodeInput>
        <Box className={classes.resend}>
          <CustomButton
            tone="text"
            buttonSize="M"
            fullWidth={false}
            disabled={cooldown > 0 || isSending || loading}
            onClick={resend}
          >
            {isSending ? "送信中…" : `認証コードを再送する${cooldown > 0 ? `（あと${cooldown}秒）` : ""}`}
          </CustomButton>
          <Typography className={contentClasses.caption}>6桁そろうと、自動で確認します。</Typography>
        </Box>
        {error && digits.every(Boolean) && (
          <CustomButton
            tone="outline"
            disabled={loading}
            onClick={() => {
              attemptedCode.current = "";
              void confirmCode(digits.join(""));
            }}
          >
            もう一度確認する
          </CustomButton>
        )}
      </Box>
    );

  const displayValue = (field: ProfileField) => {
    if (field.key === "birthday")
      return values.birth_year && values.birth_month ? `${values.birth_year}年${values.birth_month}月` : "";
    const value = values[field.key];
    if (value === "" || value == null) return "";
    if (field.options) return field.options.find((option) => option.value === value)?.label || "";
    const display =
      field.numeric && field.unit && Number.isFinite(Number(value)) ? Number(value).toLocaleString("ja-JP") : value;
    return `${display}${field.unit || ""}`;
  };
  return (
    <>
      <AuthErrorSummary message={smsError || error} />
      {section === "investment" && (
        <>
          <Typography className={classes.label}>興味のある項目</Typography>
          <Box className={classes.chips}>
            {masterInterestedPropertyTypes?.map((type) => (
              <CustomChip
                key={type.id}
                outlined
                label={type.name}
                selected={(values.interested_property_type_ids as number[]).includes(type.id)}
                disabled={propertyTypesLoading || loading}
                onClick={() => {
                  const selected = values.interested_property_type_ids as number[];
                  setValue(
                    "interested_property_type_ids",
                    selected.includes(type.id) ? selected.filter((id) => id !== type.id) : [...selected, type.id]
                  );
                }}
              />
            ))}
          </Box>
          {errors.interested_property_type_ids && <FormErrorText>{errors.interested_property_type_ids}</FormErrorText>}
        </>
      )}
      {fields.map((field) => (
        <SettingsFormRow
          key={field.key}
          label={field.label}
          optional={field.optional}
          value={displayValue(field)}
          placeholder={field.options || field.key === "birthday" ? "未選択" : "未入力"}
          error={field.key === "birthday" ? errors.birth_year || errors.birth_month : errors[field.key]}
          disabled={loading || isSending}
          onClick={() => setEditing(field)}
        />
      ))}
      <Box className={contentClasses.actions}>
        <CustomButton
          buttonSize="L"
          disabled={loading || isSending || (section === "investment" && propertyTypesLoading)}
          onClick={save}
        >
          {loading ? "保存中…" : isSending ? "送信中…" : "保存する"}
        </CustomButton>
      </Box>
      <BottomSheet
        open={!!editing}
        title={editing?.label || "入力"}
        onOpen={() => undefined}
        onClose={() => setEditing(null)}
        footer={
          !editing?.options && (
            <CustomButton buttonSize="L" onClick={() => setEditing(null)}>
              決定する
            </CustomButton>
          )
        }
      >
        {editing?.key === "birthday" ? (
          <Box className={classes.birthday}>
            <TextField
              select
              label="年"
              variant="outlined"
              value={values.birth_year}
              onChange={(event) => setValue("birth_year", Number(event.target.value))}
              SelectProps={{ native: true }}
            >
              <option value={0}>未選択</option>
              {YEAR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}年
                </option>
              ))}
            </TextField>
            <TextField
              select
              label="月"
              variant="outlined"
              value={values.birth_month}
              onChange={(event) => setValue("birth_month", Number(event.target.value))}
              SelectProps={{ native: true }}
            >
              <option value={0}>未選択</option>
              {MONTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}月
                </option>
              ))}
            </TextField>
          </Box>
        ) : editing?.options ? (
          <Box className={classes.options} role="listbox" aria-label={editing.label}>
            {editing.options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={values[editing.key] === option.value}
                className={`${classes.option} ${values[editing.key] === option.value ? classes.selected : ""}`}
                onClick={() => {
                  setValue(editing.key, option.value);
                  setEditing(null);
                }}
              >
                {option.label}
              </button>
            ))}
          </Box>
        ) : (
          editing && (
            <TextField
              className={classes.input}
              label={editing.label}
              variant="outlined"
              value={values[editing.key]}
              autoFocus
              multiline={editing.multiline}
              minRows={editing.multiline ? 3 : undefined}
              inputProps={{
                "aria-label": editing.label,
                maxLength: editing.maxLength,
                inputMode: editing.numeric ? "numeric" : undefined,
              }}
              InputProps={{
                endAdornment: editing.unit ? <InputAdornment position="end">{editing.unit}</InputAdornment> : undefined,
              }}
              onChange={(event) => setValue(editing.key, event.target.value)}
            />
          )
        )}
      </BottomSheet>
    </>
  );
};
